import { useEffect, useState } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import SidePanel from '../SidePanel';
import Dropdown from '../receptura/Dropdown';
import { SECTION_COLOR, csrf } from './ui';

/**
 * Úprava měřicího bodu — inspektor přes třetí sloupec (norma §1f), na mobilu
 * modal. Typ, chování hodnoty, hlídání, umístění, čidlo a verze limitů.
 */

// Limit 0 je platná hodnota (lednice 0–5 °C) — `hodnota || null` ji zahodil
// a nový limit se uložil bez minima.
const maHodnotu = (v) => v !== '' && v != null;
const hodnotaLimitu = (v) => (maHodnotu(v) ? v : null);

export default function BodEditor({ parametr, onClose, isDark, iStyle, mutedColor, isMobile, topOffset = 0 }) {
    const isNew = !parametr;
    const divider = `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}`;
    const [form, setForm] = useState({
        nazev: parametr?.nazev || '',
        jednotka: parametr?.jednotka || '',
        popis: parametr?.popis || '',
        instrukce_mereni: parametr?.instrukce_mereni || '',
        aktivni: parametr?.aktivni !== false,
        // Měřicí bod (lednice, mrazák, měřidlo) — jak často a kdo měří, čidlo
        typ: parametr?.typ || '',
        charakter: parametr?.charakter || '',
        frekvence: parametr?.frekvence || '',
        kariera_id: parametr?.kariera_id ?? '',
        umisteni: parametr?.umisteni || '',
        cidlo_topic: parametr?.cidlo_topic || '',
        cidlo_klic: parametr?.cidlo_klic || '',
        // Zdroj hodnot: ručně (checklist, Zapsat měření) nebo kanál uzlu pobočky
        kanal_id: parametr?.kanal_id ?? '',
        kanal_nasobek: parametr?.kanal_nasobek != null && parametr.kanal_nasobek !== 1 ? String(parametr.kanal_nasobek) : '',
        kanal_interval_min: String(parametr?.kanal_interval_min ?? 10),
        kanal_agregace: parametr?.kanal_agregace || 'prumer',
    });
    const [kanaly, setKanaly] = useState(null);
    useEffect(() => {
        fetch('/api/uzly/kanaly', { headers: { Accept: 'application/json' } }).then(r => (r.ok ? r.json() : [])).then(setKanaly).catch(() => setKanaly([]));
    }, []);
    const vybranyKanal = kanaly?.find(k => String(k.id) === String(form.kanal_id)) || null;
    // Čítač ve Wh na bod v kWh → násobek 0,001 navrhne sám
    const vyberKanal = (id) => {
        const k = kanaly?.find(x => String(x.id) === String(id));
        setForm(f => ({
            ...f, kanal_id: id,
            // Čítač (energie) = poslední stav za hodinu; ostatní průměr za 10 min (jde změnit)
            ...(k && !f.kanal_id ? (k.druh === 'citac' ? { kanal_agregace: 'posledni', kanal_interval_min: '60' } : { kanal_agregace: 'prumer', kanal_interval_min: '10' }) : {}),
            kanal_nasobek: k && k.jednotka === 'Wh' && /kwh/i.test(f.jednotka) && !f.kanal_nasobek ? '0.001' : f.kanal_nasobek,
            jednotka: f.jednotka || (k?.jednotka === 'Wh' ? 'kWh' : k?.jednotka || ''),
        }));
    };
    // Nová lednice / mrazák: jednotka a výchozí limit podle HACCP (jde změnit)
    const zmenTyp = (typ) => {
        setForm(f => ({ ...f, typ, jednotka: ['lednice', 'mrazak'].includes(typ) && !f.jednotka ? '°C' : f.jednotka,
            charakter: f.charakter || (['lednice', 'mrazak'].includes(typ) ? 'rozmezi' : typ === 'meridlo' ? 'rostouci' : f.charakter),
            frekvence: ['lednice', 'mrazak'].includes(typ) && !f.frekvence ? 'tydne' : f.frekvence }));
        if (isNew && !maHodnotu(limitForm.min_hodnota) && !maHodnotu(limitForm.max_hodnota)) {
            if (typ === 'lednice') setLimitForm(l => ({ ...l, min_hodnota: '0', max_hodnota: '5' }));
            if (typ === 'mrazak') setLimitForm(l => ({ ...l, max_hodnota: '-18' }));
        }
    };
    const [limitForm, setLimitForm] = useState({ min_hodnota: '', max_hodnota: '', cil_hodnota: '', instrukce_min: '', instrukce_max: '' });
    const [limity, setLimity] = useState([]);
    const [showLimitForm, setShowLimitForm] = useState(false);
    const [saving, setSaving] = useState(false);
    const [savingLimit, setSavingLimit] = useState(false);

    const fetchLimity = () => {
        if (!parametr?.id) return;
        fetch(`/api/checklisty/parametry/${parametr.id}/limity`).then(r => r.json()).then(setLimity).catch(() => {});
    };
    useEffect(() => { fetchLimity(); }, [parametr?.id]);

    const handleSave = async () => {
        if (!form.nazev.trim()) return;
        setSaving(true);
        const method = isNew ? 'POST' : 'PUT';
        const url = isNew ? '/api/checklisty/parametry' : `/api/checklisty/parametry/${parametr.id}`;
        const body = {
            ...form, jednotka: form.jednotka || null, popis: form.popis || null,
            typ: form.typ || null, charakter: form.charakter || null, frekvence: form.frekvence || null,
            kariera_id: form.kariera_id ? Number(form.kariera_id) : null,
            umisteni: form.umisteni || null, cidlo_topic: form.cidlo_topic || null, cidlo_klic: form.cidlo_klic || null,
            kanal_id: form.kanal_id ? Number(form.kanal_id) : null,
            kanal_nasobek: form.kanal_nasobek ? Number(String(form.kanal_nasobek).replace(',', '.')) : 1,
            kanal_interval_min: Number(form.kanal_interval_min) || 10,
            kanal_agregace: form.kanal_agregace || 'prumer',
        };

        if (isNew && [limitForm.min_hodnota, limitForm.max_hodnota, limitForm.cil_hodnota].some(maHodnotu)) {
            Object.assign(body, {
                min_hodnota: hodnotaLimitu(limitForm.min_hodnota),
                max_hodnota: hodnotaLimitu(limitForm.max_hodnota),
                cil_hodnota: hodnotaLimitu(limitForm.cil_hodnota),
                instrukce_min: limitForm.instrukce_min || null,
                instrukce_max: limitForm.instrukce_max || null,
            });
        }

        const res = await fetch(url, {
            method,
            headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrf() },
            body: JSON.stringify(body),
        });
        setSaving(false);
        if (res.ok) onClose();
    };

    // Formulář limitu slouží pro novou verzi i pro opravu existující
    // (`upravovanyLimit` = id verze, jinak se zakládá nová od dneška).
    const [upravovanyLimit, setUpravovanyLimit] = useState(null);
    const [chybaLimitu, setChybaLimitu] = useState(null);
    const otevriLimit = (l, idUpravy = null) => {
        setLimitForm({
            min_hodnota: l?.min_hodnota ?? '',
            max_hodnota: l?.max_hodnota ?? '',
            cil_hodnota: l?.cil_hodnota ?? '',
            instrukce_min: l?.instrukce_min ?? '',
            instrukce_max: l?.instrukce_max ?? '',
        });
        setUpravovanyLimit(idUpravy);
        setChybaLimitu(null);
        setShowLimitForm(true);
    };
    const zavriLimit = () => {
        setShowLimitForm(false);
        setUpravovanyLimit(null);
        setChybaLimitu(null);
        setLimitForm({ min_hodnota: '', max_hodnota: '', cil_hodnota: '', instrukce_min: '', instrukce_max: '' });
    };

    const handleSaveLimit = async () => {
        if (![limitForm.min_hodnota, limitForm.max_hodnota, limitForm.cil_hodnota].some(maHodnotu)) return;
        setSavingLimit(true);
        const hodnoty = {
            min_hodnota: hodnotaLimitu(limitForm.min_hodnota),
            max_hodnota: hodnotaLimitu(limitForm.max_hodnota),
            cil_hodnota: hodnotaLimitu(limitForm.cil_hodnota),
            instrukce_min: limitForm.instrukce_min || null,
            instrukce_max: limitForm.instrukce_max || null,
        };
        const res = await fetch(`/api/checklisty/parametry/${parametr.id}/limity${upravovanyLimit ? `/${upravovanyLimit}` : ''}`, {
            method: upravovanyLimit ? 'PUT' : 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-TOKEN': csrf() },
            body: JSON.stringify(upravovanyLimit ? hodnoty : { platnost_od: new Date().toISOString().split('T')[0], ...hodnoty }),
        });
        setSavingLimit(false);
        if (res.ok) {
            zavriLimit();
            fetchLimity();
        } else {
            const j = await res.json().catch(() => ({}));
            setChybaLimitu(j.error || j.message || 'Limit se nepodařilo uložit.');
        }
    };

    const smazLimit = async (l) => {
        const predchozi = limity.filter(x => x.id !== l.id)[0];
        const otazka = l.je_aktualni
            ? (predchozi
                ? 'Smazat aktuální limit? Znovu začne platit předchozí verze. Už zapsaná měření si své vyhodnocení nechají.'
                : 'Smazat jediný limit? Bod pak nebude mít žádný limit a měření se nebudou hlídat.')
            : 'Smazat tuto verzi limitu z historie?';
        if (!confirm(otazka)) return;
        const res = await fetch(`/api/checklisty/parametry/${parametr.id}/limity/${l.id}`, {
            method: 'DELETE', headers: { Accept: 'application/json', 'X-CSRF-TOKEN': csrf() },
        });
        if (res.ok) {
            if (upravovanyLimit === l.id) zavriLimit();
            fetchLimity();
        } else {
            alert('Limit se nepodařilo smazat.');
        }
    };

    const handleDelete = async () => {
        if (!confirm('Smazat parametr?')) return;
        await fetch(`/api/checklisty/parametry/${parametr.id}`, { method: 'DELETE', headers: { 'X-CSRF-TOKEN': csrf() } });
        onClose();
    };

    const innerGlass = isDark
        ? { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 9 }
        : { background: 'var(--surface-base)', border: '1px solid var(--border-default)', borderRadius: 9 };

    return (
        <SidePanel open onClose={onClose} title={isNew ? 'Nový měřicí bod' : `Upravit: ${parametr.nazev}`}
            isDark={isDark} isMobile={isMobile} width={440} modal={false} accent={SECTION_COLOR} topOffset={topOffset}
            footer={(
                <div style={{ padding: '12px 20px', borderTop: divider, flexShrink: 0, display: 'flex', gap: 8, justifyContent: 'space-between' }}>
                    <div>
                        {!isNew && (
                            <button onClick={handleDelete} style={{ fontSize: 12, padding: '8px 14px', borderRadius: 7, color: '#EF4444', background: 'rgba(239,68,68,0.08)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                                <Trash2 size={13} />Smazat
                            </button>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={onClose} style={{ padding: '8px 16px', borderRadius: 7, fontSize: 13, color: mutedColor, background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)', border: 'none', cursor: 'pointer' }}>Zrušit</button>
                        <button onClick={handleSave} disabled={saving || !form.nazev.trim()}
                            style={{ padding: '8px 20px', borderRadius: 7, fontSize: 13, fontWeight: 600, background: SECTION_COLOR, color: '#fff', border: 'none', cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
                            {saving ? 'Ukládám...' : (isNew ? 'Vytvořit' : 'Uložit')}
                        </button>
                    </div>
                </div>
            )}>
            <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                {/* Body — scrolluje tělo SidePanelu, ne tenhle obal. Vlastní
                    `overflow: auto` tu byl prázdný scroller: globální CSS mu dá
                    `overscroll-behavior: none`, kolečko v něm skončí a panel
                    se nepohnul — limity dole nešly vůbec otevřít. */}
                <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <div style={{ display: 'grid', gap: 10, gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr' }}>
                        <div><label style={{ fontSize: 11, fontWeight: 500, marginBottom: 4, display: 'block', color: mutedColor }}>Název *</label>
                            <input value={form.nazev} onChange={e => setForm(f => ({ ...f, nazev: e.target.value }))} style={iStyle} placeholder="např. Teplota fritézy" /></div>
                        <div><label style={{ fontSize: 11, fontWeight: 500, marginBottom: 4, display: 'block', color: mutedColor }}>Jednotka</label>
                            <input value={form.jednotka} onChange={e => setForm(f => ({ ...f, jednotka: e.target.value }))} style={iStyle} placeholder="°C, bar, l, ks..." /></div>
                    </div>
                    {!isNew && (
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                            <input type="checkbox" checked={form.aktivni} onChange={e => setForm(f => ({ ...f, aktivni: e.target.checked }))} className="accent-cyan-500" style={{ width: 16, height: 16 }} />
                            <span style={{ fontSize: 12, fontWeight: 500, color: form.aktivni ? '#10B981' : '#EF4444' }}>{form.aktivni ? 'Aktivní' : 'Neaktivní'}</span>
                        </label>
                    )}
                    <div><label style={{ fontSize: 11, fontWeight: 500, marginBottom: 4, display: 'block', color: mutedColor }}>Popis</label>
                        <textarea value={form.popis} onChange={e => setForm(f => ({ ...f, popis: e.target.value }))} style={{ ...iStyle, resize: 'none', minHeight: 48 }} placeholder="Popis parametru..." /></div>
                    <div><label style={{ fontSize: 11, fontWeight: 500, marginBottom: 4, display: 'block', color: mutedColor }}>Instrukce k měření</label>
                        <textarea value={form.instrukce_mereni || ''} onChange={e => setForm(f => ({ ...f, instrukce_mereni: e.target.value }))} style={{ ...iStyle, resize: 'none', minHeight: 48 }} placeholder="Jak změřit hodnotu — kde, čím, jak..." /></div>

                    {/* Měřicí bod */}
                    <div style={{ padding: '14px 16px', borderRadius: 9, background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'}`, display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>Měřicí bod</label>
                        <div style={{ display: 'grid', gap: 10, gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr' }}>
                            <div><label style={{ fontSize: 11, fontWeight: 500, marginBottom: 4, display: 'block', color: mutedColor }}>Co se měří</label>
                                <Dropdown isDark={isDark} value={form.typ} onChange={zmenTyp} placeholder="— není měřicí bod —"
                                    options={[{ value: '', label: '— není měřicí bod —' }, { value: 'lednice', label: 'Lednice' }, { value: 'mrazak', label: 'Mrazák' }, { value: 'meridlo', label: 'Měřidlo (voda, plyn…)' }, { value: 'jine', label: 'Jiné' }]} /></div>
                            <div><label style={{ fontSize: 11, fontWeight: 500, marginBottom: 4, display: 'block', color: mutedColor }}>Jak se hodnota chová</label>
                                <Dropdown isDark={isDark} value={form.charakter} onChange={v => setForm(f => ({ ...f, charakter: v }))} placeholder="Podle typu"
                                    options={[
                                        { value: '', label: 'Podle typu' },
                                        { value: 'rozmezi', label: 'Drží se v rozmezí', popis: 'teplota lednice — hlídá limit, při zadávání pruh s pásmem' },
                                        { value: 'rostouci', label: 'Stále roste', popis: 'stav měřidla — nejde zpátky, přírůstek jako běžně, chytí překlep' },
                                        { value: 'klesajici', label: 'Stále klesá', popis: 'zásoba, úbytek — zrcadlově' },
                                        { value: 'volna', label: 'Volná', popis: 'jen porovná s minulou hodnotou' },
                                    ]} /></div>
                            <div><label style={{ fontSize: 11, fontWeight: 500, marginBottom: 4, display: 'block', color: mutedColor }}>Hlídat, že se změří</label>
                                <Dropdown isDark={isDark} value={form.frekvence} onChange={v => setForm(f => ({ ...f, frekvence: v }))} placeholder="Nehlídat"
                                    options={[{ value: '', label: 'Nehlídat' }, { value: 'denne', label: 'Každý den' }, { value: 'tydne', label: 'Každý týden' }]} /></div>
                            <div style={{ gridColumn: isMobile ? 'auto' : '1 / -1' }}><label style={{ fontSize: 11, fontWeight: 500, marginBottom: 4, display: 'block', color: mutedColor }}>Umístění</label>
                                <input value={form.umisteni} onChange={e => setForm(f => ({ ...f, umisteni: e.target.value }))} style={iStyle} placeholder="např. kuchyně, pod pultem" /></div>
                        </div>
                        {/* Zdroj hodnot — kanál uzlu pobočky místo ručního měření */}
                        <div>
                            <label style={{ fontSize: 11, fontWeight: 500, marginBottom: 4, display: 'block', color: mutedColor }}>Zdroj hodnot</label>
                            <Dropdown isDark={isDark} value={form.kanal_id ? String(form.kanal_id) : ''} onChange={vyberKanal}
                                placeholder={kanaly === null ? 'Načítám kanály…' : 'Ručně (checklist, Zapsat měření)'}
                                options={[
                                    { value: '', label: 'Ručně (checklist, Zapsat měření)' },
                                    ...Object.entries((kanaly || []).reduce((g, k) => ((g[`${k.uzel} · ${k.zarizeni}`] ||= []).push(k), g), {}))
                                        .flatMap(([skupina, ks]) => [
                                            { value: `__${skupina}`, label: skupina, hlavicka: true },
                                            ...ks.map(k => ({
                                                value: String(k.id),
                                                label: k.nazev || `${k.komponenta} / ${k.velicina}`,
                                                popis: [k.hodnota != null ? `${Number(k.hodnota).toLocaleString('cs-CZ', { maximumFractionDigits: 3 })} ${k.jednotka || ''}` : null,
                                                    k.druh === 'citac' ? 'čítač' : null,
                                                    k.body?.filter(b => b.id !== parametr?.id).length ? `napojeno: ${k.body.filter(b => b.id !== parametr?.id).map(b => b.nazev).join(', ')}` : null].filter(Boolean).join(' · '),
                                            })),
                                        ]),
                                ]} />
                        </div>
                        {form.kanal_id && (
                            <div style={{ display: 'grid', gap: 10, gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr' }}>
                                <div><label style={{ fontSize: 11, fontWeight: 500, marginBottom: 4, display: 'block', color: mutedColor }}>Zapisovat každých</label>
                                    <Dropdown isDark={isDark} value={form.kanal_interval_min} onChange={v => setForm(f => ({ ...f, kanal_interval_min: v }))}
                                        options={[['1', '1 minutu'], ['5', '5 minut'], ['10', '10 minut'], ['15', '15 minut'], ['30', '30 minut'], ['60', 'hodinu'], ['1440', 'den (půlnoc)']].map(([value, label]) => ({ value, label }))} /></div>
                                <div><label style={{ fontSize: 11, fontWeight: 500, marginBottom: 4, display: 'block', color: mutedColor }}>Hodnota za interval</label>
                                    <Dropdown isDark={isDark} value={form.kanal_agregace} onChange={v => setForm(f => ({ ...f, kanal_agregace: v }))}
                                        options={[
                                            { value: 'prumer', label: 'Průměr', popis: 'teplota, příkon — vyhladí krátké výkyvy' },
                                            { value: 'posledni', label: 'Poslední hodnota', popis: 'stav čítače (elektroměr, vodoměr)' },
                                            { value: 'maximum', label: 'Maximum', popis: 'špička — proud na fázi, nejvyšší teplota' },
                                            { value: 'minimum', label: 'Minimum', popis: 'nejnižší hodnota v intervalu' },
                                        ]} /></div>
                            </div>
                        )}
                        {form.kanal_id && (
                            <div style={{ display: 'grid', gap: 10, gridTemplateColumns: isMobile ? '1fr' : '1fr 2fr', alignItems: 'end' }}>
                                <div><label style={{ fontSize: 11, fontWeight: 500, marginBottom: 4, display: 'block', color: mutedColor }}>Násobek</label>
                                    <input inputMode="decimal" value={form.kanal_nasobek} onChange={e => setForm(f => ({ ...f, kanal_nasobek: e.target.value }))} style={iStyle} placeholder="1" /></div>
                                <p style={{ fontSize: 11.5, color: mutedColor, margin: 0, lineHeight: 1.45 }}>
                                    {vybranyKanal?.hodnota != null
                                        ? `Teď ${Number(vybranyKanal.hodnota * (Number(String(form.kanal_nasobek || 1).replace(',', '.')) || 1)).toLocaleString('cs-CZ', { maximumFractionDigits: 3 })} ${form.jednotka || ''}`
                                        : 'Kanál zatím neposlal hodnotu.'}
                                    {vybranyKanal?.jednotka === 'Wh' ? ' · čítač je ve Wh, na kWh násobek 0,001' : ''}
                                </p>
                            </div>
                        )}
                        {/* Starší přímé napojení čidla přes MQTT — jen když už ho bod má */}
                        {(form.cidlo_topic || form.cidlo_klic) && (
                            <details>
                                <summary style={{ fontSize: 11.5, color: mutedColor, cursor: 'pointer' }}>Čidlo přímo přes MQTT (starší cesta — nahrazuje ji kanál uzlu)</summary>
                                <div style={{ display: 'grid', gap: 10, gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr', marginTop: 8 }}>
                                    <input value={form.cidlo_topic} onChange={e => setForm(f => ({ ...f, cidlo_topic: e.target.value }))} style={iStyle} placeholder="shelly-lednice/events/rpc" />
                                    <input value={form.cidlo_klic} onChange={e => setForm(f => ({ ...f, cidlo_klic: e.target.value }))} style={iStyle} placeholder="temperature:100" />
                                </div>
                            </details>
                        )}
                        <p style={{ fontSize: 11.5, color: mutedColor, margin: 0, lineHeight: 1.45 }}>
                            {form.kanal_id
                                ? 'Hodnoty posílá uzel pobočky po minutách — za každý interval se do historie zapíše jedno měření a porovná se s limitem (mimo limit = odchylka a upozornění vedení). Když kanál hodinu nic nepošle, dozví se to vedení.'
                                : 'Kdo a kdy měří, určuje checklist ve fázi směny (povinná položka, u týdenního měření s opakováním např. jen v pondělí).'}
                            {!form.kanal_id && form.frekvence ? ` Když se ${form.frekvence === 'denne' ? 'za den' : 'za týden'} nic nenaměří, dozví se to vedení. Kdo má bod v checklistu po někom, kdo ho už změřil, dostane položku rovnou vyplněnou.` : ''}
                        </p>
                    </div>

                    {/* Limity */}
                    <div style={{ padding: '14px 16px', borderRadius: 9, background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'}` }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>
                                {isNew ? 'Počáteční limit' : 'Limity'}
                            </label>
                            {!isNew && !showLimitForm && (
                                // Plný aktuální limit z historie (i cíl a instrukce),
                                // detail bodu nese jen min/max.
                                <button onClick={() => otevriLimit(limity.find(l => l.je_aktualni) || parametr?.aktualni_limit)}
                                    style={{ fontSize: 11, background: 'rgba(12,192,223,0.15)', color: SECTION_COLOR, border: 'none', borderRadius: 5, padding: '3px 10px', cursor: 'pointer', fontWeight: 600 }}>
                                    + Nový limit
                                </button>
                            )}
                        </div>

                        {(isNew || showLimitForm) && (
                            <div style={{ marginBottom: limity.length > 0 ? 12 : 0 }}>
                                {!isNew && (
                                    <p style={{ fontSize: 11, color: mutedColor, margin: '0 0 8px' }}>
                                        {upravovanyLimit
                                            ? 'Oprava této verze — platnost zůstane stejná, změna platí pro další měření.'
                                            : 'Nová verze limitu platí od dneška, předchozí se uzavře.'}
                                    </p>
                                )}
                                <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(3, 1fr)' }}>
                                    <div>
                                        <label style={{ fontSize: 10, marginBottom: 2, display: 'block', color: '#3B82F6', fontWeight: 600 }}>Min</label>
                                        <input type="number" step="any" value={limitForm.min_hodnota} onChange={e => setLimitForm(f => ({ ...f, min_hodnota: e.target.value }))}
                                            style={{ ...iStyle, fontSize: 12, padding: '6px 8px', borderColor: '#3B82F630' }} placeholder="—" />
                                    </div>
                                    <div>
                                        <label style={{ fontSize: 10, marginBottom: 2, display: 'block', color: '#10B981', fontWeight: 600 }}>Cíl</label>
                                        <input type="number" step="any" value={limitForm.cil_hodnota} onChange={e => setLimitForm(f => ({ ...f, cil_hodnota: e.target.value }))}
                                            style={{ ...iStyle, fontSize: 12, padding: '6px 8px', borderColor: '#10B98130' }} placeholder="—" />
                                    </div>
                                    <div>
                                        <label style={{ fontSize: 10, marginBottom: 2, display: 'block', color: '#EF4444', fontWeight: 600 }}>Max</label>
                                        <input type="number" step="any" value={limitForm.max_hodnota} onChange={e => setLimitForm(f => ({ ...f, max_hodnota: e.target.value }))}
                                            style={{ ...iStyle, fontSize: 12, padding: '6px 8px', borderColor: '#EF444430' }} placeholder="—" />
                                    </div>
                                </div>
                                <div style={{ display: 'grid', gap: 8, marginTop: 8, gridTemplateColumns: '1fr 1fr' }}>
                                    <div>
                                        <label style={{ fontSize: 10, marginBottom: 2, display: 'block', color: '#3B82F6', fontWeight: 500 }}>Instrukce při NÍZKÉM limitu</label>
                                        <input value={limitForm.instrukce_min} onChange={e => setLimitForm(f => ({ ...f, instrukce_min: e.target.value }))}
                                            style={{ ...iStyle, fontSize: 11, padding: '5px 8px' }} placeholder="Co dělat když je pod minimum..." />
                                    </div>
                                    <div>
                                        <label style={{ fontSize: 10, marginBottom: 2, display: 'block', color: '#EF4444', fontWeight: 500 }}>Instrukce při VYSOKÉM limitu</label>
                                        <input value={limitForm.instrukce_max} onChange={e => setLimitForm(f => ({ ...f, instrukce_max: e.target.value }))}
                                            style={{ ...iStyle, fontSize: 11, padding: '5px 8px' }} placeholder="Co dělat když je nad maximum..." />
                                    </div>
                                </div>
                                {chybaLimitu && <p style={{ fontSize: 11, color: '#EF4444', margin: '8px 0 0' }}>{chybaLimitu}</p>}
                                {!isNew && showLimitForm && (
                                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                        <button onClick={handleSaveLimit} disabled={savingLimit}
                                            style={{ fontSize: 11, padding: '4px 12px', borderRadius: 5, background: SECTION_COLOR, color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600, opacity: savingLimit ? 0.6 : 1 }}>
                                            {savingLimit ? 'Ukládám...' : (upravovanyLimit ? 'Uložit opravu' : 'Uložit limit')}
                                        </button>
                                        <button onClick={zavriLimit}
                                            style={{ fontSize: 11, padding: '4px 12px', borderRadius: 5, color: mutedColor, background: 'transparent', border: 'none', cursor: 'pointer' }}>
                                            Zrušit
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Historie limitů */}
                        {!isNew && limity.length > 0 && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                <label style={{ fontSize: 10, fontWeight: 500, color: mutedColor }}>Historie</label>
                                {limity.map(l => (
                                    <div key={l.id} style={{
                                        display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 6, fontSize: 11,
                                        background: l.je_aktualni ? (isDark ? 'rgba(12,192,223,0.08)' : 'rgba(12,192,223,0.05)') : 'transparent',
                                        border: `1px solid ${l.je_aktualni ? 'rgba(12,192,223,0.25)' : isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'}`,
                                    }}>
                                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: l.je_aktualni ? SECTION_COLOR : isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)', flexShrink: 0 }} />
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                {l.min_hodnota != null && <span style={{ color: '#3B82F6' }}>Min {l.min_hodnota}</span>}
                                                {l.cil_hodnota != null && <span style={{ color: '#10B981' }}>Cíl {l.cil_hodnota}</span>}
                                                {l.max_hodnota != null && <span style={{ color: '#EF4444' }}>Max {l.max_hodnota}</span>}
                                            </div>
                                            {l.instrukce_min && <p style={{ color: '#3B82F6', marginTop: 1, fontSize: 10, margin: '1px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Min: {l.instrukce_min}</p>}
                                            {l.instrukce_max && <p style={{ color: '#EF4444', marginTop: 1, fontSize: 10, margin: '1px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Max: {l.instrukce_max}</p>}
                                        </div>
                                        <span style={{ color: mutedColor, flexShrink: 0, fontSize: 10 }}>
                                            {new Date(l.platnost_od).toLocaleDateString('cs')}
                                            {l.platnost_do ? ` — ${new Date(l.platnost_do).toLocaleDateString('cs')}` : ' — nyní'}
                                        </span>
                                        {l.autor && <span style={{ color: mutedColor, flexShrink: 0, fontSize: 10 }}>{l.autor.jmeno}</span>}
                                        <button type="button" title="Upravit tuto verzi" aria-label="Upravit limit" onClick={() => otevriLimit(l, l.id)}
                                            style={{ flexShrink: 0, width: 26, height: 26, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: 6, border: 'none', cursor: 'pointer', background: upravovanyLimit === l.id ? 'rgba(12,192,223,0.18)' : 'transparent', color: upravovanyLimit === l.id ? SECTION_COLOR : mutedColor }}>
                                            <Pencil size={13} />
                                        </button>
                                        <button type="button" title="Smazat tuto verzi" aria-label="Smazat limit" onClick={() => smazLimit(l)}
                                            style={{ flexShrink: 0, width: 26, height: 26, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: 6, border: 'none', cursor: 'pointer', background: 'transparent', color: '#EF4444' }}>
                                            <Trash2 size={13} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

            </div>
        </SidePanel>
    );
}
